export const SUPPORT_INFO_ENTRIES = [
  {
    id: "about",
    title: "About Aeterna Invicta",
    summary:
      "What the game is, how it is played, and who makes it.",
    blocks: [
      "**Aeterna Invicta** is a free-to-play, online fantasy collectible card game. Choose an Aspect, build Equipment and Ability decks, gear up your champion, and duel one-on-one until one side falls.",
      "*Become eternal and uncontested!*",
      { type: "heading", text: "Ways to Play" },
      {
        type: "list",
        items: [
          "**VS CPU.** Practice against computer opponents at your own pace.",
          "**VS Human.** Enter the queue to be matched against other players in timed duels.",
          "**Draft.** Build a deck from drafted cards and see how far you can go.",
          "**Adventure.** Travel a map alone or with a party, fight monsters, and collect effects along the way.",
          "**Tutorials.** Learn the rules step by step, from your first draw to leveling up into a Trait.",
          "**Library and Rulebook.** Browse every card, keyword, and rule in the game.",
        ],
      },
      { type: "heading", text: "What You Need" },
      {
        type: "list",
        items: [
          "An internet connection. Matches, collections, and progress are saved online.",
          "A guest account or a registered account. Guest play does not require an email address.",
          "No purchase is required to play.",
        ],
      },
      { type: "heading", text: "Platforms" },
      "Aeterna Invicta is available on the web at aeternainvicta.com and as apps for Android and iOS. The apps load the same online game, so updates, balance changes, and new cards usually arrive without needing an app store update.",
      { type: "heading", text: "Developer and Publisher" },
      {
        type: "list",
        items: [
          "**Designer and Developer:** randomnewb",
          "**Publisher:** ATN DEV",
          "**Email:** aeternainvicta.ccg@gmail.com",
          "**Website:** aeternainvicta.com",
        ],
      },
    ],
  },
  {
    id: "ageRating",
    title: "Age Rating and Content Advisory",
    summary:
      "Intended for players 13 and up. Contains fantasy violence and depictions of blood.",
    blocks: [
      "Aeterna Invicta is intended for players **13 years of age and older**. It is not designed for or directed to children under 13.",
      "Age ratings shown on each app store are assigned through that store's rating process and may differ by region.",
      { type: "heading", text: "Content Descriptors" },
      {
        type: "list",
        items: [
          "**Fantasy Violence.** Matches are battles between fantasy champions, creatures, and minions using weapons, armor, spells, and abilities. Attacks, damage, and defeat are shown through illustrated card art, attack animations, and sound effects.",
          "**Depictions of Blood.** Some card art and text show blood, such as wounds, bleeding effects, and dark magic that is paid for in blood or life.",
        ],
      },
      { type: "heading", text: "Interactive Elements" },
      {
        type: "list",
        items: [
          "**Users Interact.** Players can be matched against other players online. Optional text chat lets eligible players send messages to each other. Chat is off until you opt in.",
          "**Shares Info.** Your username, name color, rankings, match records, and shared decks can be seen by other players.",
          "**Optional Advertisements.** After some matches in the Android or iOS app you may choose to watch an advertisement for extra foil unlocks. Advertisements are not offered in web browsers and are never shown unless you choose to watch one.",
          "**In-Game Currency.** Currency is earned by playing and can be spent in the Marketplace, Customize, and Draft. Currency and card unlocks cannot currently be bought with real money.",
        ],
      },
      { type: "heading", text: "For Parents and Guardians" },
      {
        type: "list",
        items: [
          "Chat is opt-in. It is only available on registered accounts that are at least 24 hours old or have completed 5 games, and the player must agree to the chat rules first.",
          "Chat can be turned off at any time from **Settings > Chat**. Turning it back on requires accepting the chat rules again.",
          "Players can **Block** anyone to hide their chat messages and match emotes and stop their challenges, and can **Report** anyone directly from their profile or the in-match player preview.",
          "Guest accounts do not require an email address and are deleted after 24 hours unless a registered account is created.",
          "Accounts can be permanently deleted at any time from **Settings > Account > Delete Account** or at aeternainvicta.com/delete-account.",
          "Questions or concerns can be sent to aeternainvicta.ccg@gmail.com or through **Contact for Support** on this page.",
        ],
      },
    ],
  },
  {
    id: "community",
    title: "Community Guidelines and Safety",
    summary:
      "How to stay safe, what is not allowed, and how to report a problem.",
    blocks: [
      "Aeterna Invicta should be a fun, fair, and welcoming place for everyone 13 and up. These guidelines apply to chat, usernames, shared decks, emotes, and any other way you interact with other players.",
      { type: "heading", text: "Optional Chat" },
      {
        type: "list",
        items: [
          "Chat is off until you opt in and agree to the chat rules.",
          "Chat requires a registered account that is at least 24 hours old or has completed 5 games.",
          "Messages are written by players and do not represent ATN DEV. Chat is not necessarily reviewed before it appears.",
          "Do not assume chat is private or confidential.",
          "You can turn chat off at any time from **Settings > Chat**. To turn it back on, you will need to accept the chat rules again.",
        ],
      },
      { type: "heading", text: "Stay Safe" },
      {
        type: "list",
        items: [
          "Never share your password, real name, address, school, phone number, financial information, or other personal details.",
          "ATN DEV will never ask for your password in chat.",
          "Do not agree to meet anyone you only know from the game.",
          "If someone makes you uncomfortable, stop replying, block them, and report them.",
        ],
      },
      { type: "heading", text: "Not Allowed" },
      {
        type: "list",
        items: [
          "Threats of violence, harassment, bullying, or targeted abuse.",
          "Hate speech or discrimination based on race, ethnicity, religion, gender, sexual orientation, disability, or other identity.",
          "Sexual or sexually explicit content, or any attempt to exploit, groom, or endanger minors.",
          "Offensive, hateful, or impersonating usernames.",
          "Scams, phishing, or attempts to obtain another player's account.",
          "Spam, advertising, or repeated disruptive messages.",
          "Cheating, exploiting bugs, botting, or manipulating matches or rankings.",
          "Encouraging or facilitating illegal activity, self-harm, or real-world violence.",
        ],
      },
      { type: "heading", text: "Reporting a Player" },
      {
        type: "list",
        items: [
          "Open the player's profile by tapping their name in Social, Ranking, or chat, or open their player preview during a match, then choose **Report**.",
          "Pick the category that fits best and add brief details about what happened. Reports from chat include that player's recent chat messages.",
          "Reports are private. The reported player is not told who reported them.",
          "You can also email aeternainvicta.ccg@gmail.com or use **Contact for Support** on this page.",
          "If anyone is in immediate danger, contact local emergency services first.",
        ],
      },
      { type: "heading", text: "Blocking a Player" },
      {
        type: "list",
        items: [
          "Choose **Block** on a player's profile, in the in-match player preview, or in the report window.",
          "Blocking hides that player's chat messages and match emotes from you and stops them from challenging you.",
          "Manage or remove blocks at any time from **Settings > Blocked Players**.",
        ],
      },
      { type: "heading", text: "Enforcement" },
      "ATN DEV reviews reports and may, at its discretion, warn a player, mute their chat and remove their messages, suspend an account, or permanently ban an account. Severe violations, including anything that endangers minors, may result in an immediate ban and may be reported to the appropriate authorities.",
    ],
  },
  {
    id: "terms",
    title: "Terms of Use",
    summary:
      "The rules for using the game, your account, and in-game items.",
    blocks: [
      "These Terms of Use are an agreement between you and **ATN DEV** for your use of **Aeterna Invicta** (\"the Game\") on the web, Android, and iOS. By playing, you agree to these terms, the Community Guidelines, and the Privacy Policy.",
      { type: "heading", text: "1. Eligibility" },
      "You must be at least 13 years old to play. If you are under the age of majority where you live, a parent or legal guardian should review these terms with you.",
      { type: "heading", text: "2. Accounts" },
      {
        type: "list",
        items: [
          "You can play as a guest or create a registered account. Guest accounts are deleted after 24 hours unless a registered account is created.",
          "You are responsible for keeping your password secure and for activity on your account.",
          "Do not share, sell, or transfer your account, or use another player's account.",
          "You may delete your account at any time from Settings > Account > Delete Account or at aeternainvicta.com/delete-account.",
        ],
      },
      { type: "heading", text: "3. Virtual Items and Currency" },
      {
        type: "list",
        items: [
          "Cards, foils, Currency, boards, name colors, and other in-game items are licensed to you for use in the Game. They have no real-world monetary value and cannot be exchanged for cash.",
          "Virtual items may not be sold, traded, or transferred outside the Game.",
          "Cards and items may be rebalanced, changed, or retired as the Game is updated.",
          "If real-money purchases are offered in the future, prices will be shown before purchase, payment will be handled by the app store or payment provider, and refunds will follow that provider's policies. Purchases will never provide a competitive gameplay advantage.",
        ],
      },
      { type: "heading", text: "4. Advertisements" },
      "The Game may offer optional advertisements in exchange for in-game rewards. Advertisements are provided by third parties and are only shown when you choose to watch one.",
      { type: "heading", text: "5. Fair Play and Conduct" },
      "You agree to follow the Community Guidelines. You may not cheat, use bots or unauthorized software, exploit bugs, interfere with servers, or attempt to access other players' accounts or data.",
      { type: "heading", text: "6. Enforcement" },
      "ATN DEV may warn, restrict chat, suspend, or terminate accounts that violate these terms or the Community Guidelines, with or without prior notice where permitted by law. Suspended or terminated accounts may lose access to their collection, progress, and items.",
      { type: "heading", text: "7. Changes to the Game" },
      "The Game is an online service that is updated regularly. Features, modes, cards, and rules may be added, changed, or removed at any time. The Game requires an internet connection and may occasionally be unavailable for maintenance or technical reasons.",
      { type: "heading", text: "8. Intellectual Property" },
      "The Game, including its cards, art, text, music, and software, is owned by ATN DEV and its licensors. You may share screenshots, videos, and deck lists for non-commercial purposes, but you may not copy, modify, or redistribute the Game itself.",
      { type: "heading", text: "9. Disclaimer and Limitation of Liability" },
      "The Game is provided \"as is\" and \"as available\" without warranties of any kind. To the fullest extent permitted by law, ATN DEV is not liable for indirect, incidental, or consequential damages, or for loss of data, progress, or virtual items. Nothing in these terms limits rights you have under applicable consumer protection law.",
      { type: "heading", text: "10. App Stores" },
      "If you downloaded the Game from Google Play or the Apple App Store, that store's terms also apply. Apple and Google are not responsible for the Game or its content, and are not parties to these terms.",
      { type: "heading", text: "11. Changes to These Terms" },
      "These terms may be updated from time to time. Continuing to play after an update means you accept the updated terms.",
      { type: "heading", text: "12. Contact" },
      "Questions about these terms can be sent to aeternainvicta.ccg@gmail.com.",
      "*Last updated: October 2, 2026.*",
    ],
  },
  {
    id: "accountData",
    title: "Accounts and Data Deletion",
    summary:
      "Guest play, registered accounts, and how to delete your account and data.",
    blocks: [
      { type: "heading", text: "Guest Accounts" },
      "You can play without an email address using a guest account. Guest accounts and their progress are deleted after 24 hours, or when you sign out of them, unless you create a registered account, which keeps your collection, decks, and progress.",
      { type: "heading", text: "Registered Accounts" },
      "A registered account needs only a username and password. It saves your collection, decks, rankings, achievements, and settings online so you can continue on the web, Android, or iOS. You can change your password from Settings > Account.",
      { type: "heading", text: "Deleting Your Account" },
      {
        type: "list",
        items: [
          "**In the Game:** go to **Settings > Account > Delete Account**, enter your password, and confirm with your username.",
          "**On the web:** visit https://aeternainvicta.com/delete-account. You do not need the app.",
          '**By email:** write to aeternainvicta.ccg@gmail.com with the subject "Aeterna Invicta Account Deletion" and your username. Never send your password.',
        ],
      },
      "In-game and web deletion happen immediately. Email requests are completed within 30 days. Deletion is permanent and removes your username, password, sessions, stored IP address and approximate location, collection, decks, progress, records, Currency, settings, chat messages, support tickets, match logs you played in, and activity log entries about you.",
      "Kept for a limited time: player reports about your account (12 months, for safety), operational emails already in our mailbox (90 days), support emails you sent us (up to 12 months), and backups (30 days). Reports you filed are kept without your username. Uninstalling the game does not delete your account.",
      { type: "heading", text: "Privacy Requests" },
      "To request access to, correction of, or a copy of your information, or deletion of specific data without deleting your account, email aeternainvicta.ccg@gmail.com. We may need to verify that the request comes from the account owner.",
      "*Full details: https://aeternainvicta.com/privacy*",
    ],
  },
];
